import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { financeHotspots } from '@/mocks/finance';
import { useAppState } from '@/hooks/useAppState';
import SharePreview from '@/components/feature/SharePreview';
import PrivacyBadge from '@/components/feature/PrivacyBadge';

export default function FinanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openShareModal, shareModalOpen, shareModalTarget, closeShareModal } = useAppState();

  const hotspot = financeHotspots.find((h) => h.id === id);

  if (!hotspot) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-500">内容未找到</p>
      </div>
    );
  }

  const related = hotspot.relatedIds
    ?.map((rid) => financeHotspots.find((h) => h.id === rid))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50 pb-20">
      {/* Header */}
      <header className="px-4 pt-6 pb-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}>
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate('/finance')}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-lg text-foreground-600"></i>
          </button>
          <div className="flex-1"></div>
          <div className="flex items-center gap-1">
            <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-bookmark-line text-lg text-foreground-500"></i>
            </button>
            <button
              onClick={() => openShareModal(hotspot.id)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line text-lg text-foreground-500"></i>
            </button>
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-[10px] text-foreground-400">{hotspot.source}</span>
          <span className="text-[10px] text-foreground-300">·</span>
          <span className="text-[10px] text-foreground-400">{hotspot.publishTime}</span>
          <span className="text-[10px] text-foreground-300">·</span>
          <span className="text-[10px] text-foreground-400">更新于 {hotspot.updateTime}</span>
          <PrivacyBadge type="shareable" className="ml-auto" />
        </div>

        {/* Tags */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {hotspot.tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-600 font-medium whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
            hotspot.heat === '高' ? 'bg-red-50 text-red-600' :
            hotspot.heat === '中' ? 'bg-amber-50 text-amber-600' :
            'bg-secondary-50 text-foreground-500'
          }`}>
            {hotspot.heat}热度
          </span>
        </div>

        {/* Title */}
        <h1 className="text-base font-bold text-foreground-800 leading-snug font-heading">
          {hotspot.title}
        </h1>
      </header>

      <main className="px-4 space-y-5">
        {/* 一分钟看懂 */}
        <section className="bg-background-100 rounded-xl px-4 py-4">
          <h2 className="text-xs font-semibold text-foreground-700 mb-2 font-heading">一分钟看懂</h2>
          <ul className="space-y-2">
            {hotspot.keyPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-semibold text-accent-600">{i + 1}</span>
                </span>
                <span className="text-xs text-foreground-700 leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Why important */}
        <section>
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">为什么值得关注</h2>
          <p className="text-xs text-foreground-600 leading-relaxed bg-background-50 rounded-xl px-4 py-3 border border-background-200/40">
            {hotspot.whyImportant}
          </p>
        </section>

        {/* Full content */}
        <section>
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">详细内容</h2>
          <div className="text-xs text-foreground-700 leading-relaxed whitespace-pre-line">
            {hotspot.content}
          </div>
        </section>

        {/* Related */}
        {related && related.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">相关热点</h2>
            <div className="space-y-2">
              {related.map((r) => r && (
                <button
                  key={r.id}
                  onClick={() => navigate(`/finance/${r.id}`)}
                  className="w-full text-left bg-background-50 rounded-lg px-3.5 py-3 border border-background-200/40 hover:border-accent-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] text-foreground-400">{r.source}</span>
                    <span className="text-[10px] text-foreground-300">·</span>
                    <span className="text-[10px] text-foreground-400">{r.publishTime}</span>
                  </div>
                  <p className="text-xs text-foreground-700 leading-snug">{r.title}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Source link */}
        <section className="pb-2 text-center">
          <button className="inline-flex items-center gap-1.5 text-xs text-primary-500 font-medium hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-external-link-line"></i>
            </span>
            查看原文链接
          </button>
        </section>
      </main>

      {/* Share Preview */}
      <SharePreview
        hotspot={hotspot}
        open={shareModalOpen}
        onClose={closeShareModal}
      />
    </div>
  );
}